import React from "react";

/**
 * Minimalist, high-precision vector lockup for PNB (Philippine National Bank) | Philippine Airlines
 * Matches the co-branded credit card typography and emblem layout.
 */
export function PnbLogoMark({ variant = "dark", className = "h-6 w-auto", showSubtitle = true }) {
  const goldPrimary = variant === "light" ? "#00338D" : "#E5C168";
  const subTextFill = variant === "light" ? "#0A2558" : "#D8B45A";
  const shieldBg = variant === "light" ? "#FFFFFF" : "#0A172E";

  if (!showSubtitle) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 230 78"
        fill="none"
        className={className}
        aria-label="PNB"
      >
        <rect x="4" y="6" width="64" height="64" rx="10" stroke={goldPrimary} strokeWidth="4.5" fill={shieldBg} />
        <path
          d="M16 19 C24 19, 30 15, 36 10.5 C42 15, 48 19, 56 19 L54 44 C53 55, 45 61.5, 36 65.5 C27 61.5, 19 55, 18 44 Z"
          fill={goldPrimary}
        />
        <path
          d="M18.5 21 C24.5 21, 30 17.5, 36 13.5 C42 17.5, 47.5 21, 53.5 21 L52.8 33 L19.2 33 Z"
          fill={shieldBg}
        />
        <path d="M19.5 35 L35 35 L35 63 C27.5 59.5, 20.5 53, 19.8 43.5 Z" fill="#1B4D89" />
        <path d="M37 35 L52.5 35 L52.2 43.5 C51.5 53, 44.5 59.5, 37 63 Z" fill="#C81E2B" />
        <circle cx="36" cy="34" r="8.5" fill={shieldBg} />
        <circle cx="36" cy="34" r="5.5" fill="#FDB913" />
        <circle cx="25.5" cy="25" r="2" fill="#FDB913" />
        <circle cx="36" cy="21.5" r="2" fill="#FDB913" />
        <circle cx="46.5" cy="25" r="2" fill="#FDB913" />
        <text
          x="80"
          y="59"
          fill={goldPrimary}
          fontFamily="'Inter', 'Helvetica Neue', Arial, sans-serif"
          fontSize="58"
          fontWeight="900"
          letterSpacing="1"
        >
          PNB
        </text>
      </svg>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 250 105"
      fill="none"
      className={className}
      aria-label="PNB Philippine National Bank"
    >
      <rect x="4" y="6" width="64" height="64" rx="10" stroke={goldPrimary} strokeWidth="4.5" fill={shieldBg} />
      <path
        d="M16 19 C24 19, 30 15, 36 10.5 C42 15, 48 19, 56 19 L54 44 C53 55, 45 61.5, 36 65.5 C27 61.5, 19 55, 18 44 Z"
        fill={goldPrimary}
      />
      <path
        d="M18.5 21 C24.5 21, 30 17.5, 36 13.5 C42 17.5, 47.5 21, 53.5 21 L52.8 33 L19.2 33 Z"
        fill={shieldBg}
      />
      <path d="M19.5 35 L35 35 L35 63 C27.5 59.5, 20.5 53, 19.8 43.5 Z" fill="#1B4D89" />
      <path d="M37 35 L52.5 35 L52.2 43.5 C51.5 53, 44.5 59.5, 37 63 Z" fill="#C81E2B" />
      <circle cx="36" cy="34" r="8.5" fill={shieldBg} />
      <circle cx="36" cy="34" r="5.5" fill="#FDB913" />
      <circle cx="25.5" cy="25" r="2" fill="#FDB913" />
      <circle cx="36" cy="21.5" r="2" fill="#FDB913" />
      <circle cx="46.5" cy="25" r="2" fill="#FDB913" />
      <text
        x="80"
        y="60"
        fill={goldPrimary}
        fontFamily="'Inter', 'Helvetica Neue', Arial, sans-serif"
        fontSize="60"
        fontWeight="900"
        letterSpacing="1.2"
      >
        PNB
      </text>
      <text
        x="4"
        y="94"
        fill={subTextFill}
        fontFamily="'Inter', 'Helvetica Neue', Arial, sans-serif"
        fontSize="19.5"
        fontWeight="600"
        letterSpacing="0.4"
      >
        Philippine National Bank
      </text>
    </svg>
  );
}

// Keep BdoLogoMark alias for backwards compatibility if imported anywhere
export const BdoLogoMark = PnbLogoMark;

export function PalLogoMark({ variant = "dark", className = "h-7 w-auto" }) {
  const textFill = variant === "light" ? "#06152E" : "#FFFFFF";

  return (
    <div className={`inline-flex items-center gap-2 select-none ${className}`}>
      {/* Iconic PAL Tail Fin Emblem */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 64 52"
        fill="none"
        className="h-full w-auto shrink-0 drop-shadow-sm"
        aria-hidden="true"
      >
        {/* Blue Sail */}
        <polygon points="4,48 44,4 37,48" fill="#1B3687" />
        {/* Sunburst Rays inside Blue Sail */}
        <path
          d="M25,48 L10,45 L25,45 L14,39 L27,42 L19,32 L29,39 L25,25 L32,37 L31,19 L35,35 L37,14 L38,36 L41,22 L39,48 Z"
          fill="#FDB913"
        />
        {/* Red Sail */}
        <polygon points="24,48 61,6 54,48" fill="#E11D2A" />
      </svg>
      <div
        className="flex flex-col justify-center leading-[1.05] italic font-bold tracking-tight"
        style={{ color: textFill }}
      >
        <span className="text-[12px] sm:text-[13px]">Philippine</span>
        <span className="text-[12px] sm:text-[13px]">Airlines</span>
      </div>
    </div>
  );
}

export default function BrandLockup({
  variant = "dark",
  size = "md",
  showBadge = false,
  badgeText = "Privilege Club",
  className = "",
}) {
  const sizes = {
    sm: {
      pnb: "h-6 w-auto",
      divider: "h-5",
      pal: "h-6",
      gap: "gap-2.5",
    },
    md: {
      pnb: "h-7 sm:h-8 w-auto",
      divider: "h-6 sm:h-7",
      pal: "h-6 sm:h-7",
      gap: "gap-3 sm:gap-3.5",
    },
    lg: {
      pnb: "h-8 sm:h-9 w-auto",
      divider: "h-7 sm:h-8",
      pal: "h-7 sm:h-8",
      gap: "gap-3.5 sm:gap-4",
    },
  };

  const s = sizes[size] || sizes.md;
  const dividerColor =
    variant === "light"
      ? "bg-slate-300"
      : "bg-gradient-to-b from-white/10 via-[#D4AF37]/55 to-white/10";

  return (
    <div className={`inline-flex items-center ${s.gap} select-none ${className}`}>
      <PnbLogoMark variant={variant} className={s.pnb} showSubtitle={true} />
      <span className={`w-px ${s.divider} ${dividerColor} shrink-0`} aria-hidden="true" />
      <PalLogoMark variant={variant} className={s.pal} />
      {showBadge && (
        <span
          className={`hidden lg:inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${
            variant === "light"
              ? "text-[#084C5B] bg-[#084C5B]/5 border-[#084C5B]/20"
              : "text-[#E6C770] bg-white/[0.04] border-[#D4AF37]/30"
          } ml-1`}
        >
          {badgeText}
        </span>
      )}
    </div>
  );
}
